import { CopyButton, PageHero, Section } from "../components/primitives.jsx";
import { ProtocolTabs } from "../components/tabs.jsx";
import { protocolExamples } from "../data/catalog.js";

const installCommand = `mkdir -p ~/.claude/skills/keep-straight-architecture
curl -L https://specification.architecture/skills/architecture-review-skill.md -o ~/.claude/skills/keep-straight-architecture/SKILL.md`;

const endpoints = [
  ["/data/search-index.json", "Search fixture"],
  ["/mcp/resources.json", "Resources"],
  ["/mcp/tools.json", "Tools and prompts"],
  ["/mcp/server.json", "Server capability"]
];

const stack = [
  ["Human UI", "Search, compare, copy checklists"],
  ["MCP prompts", "review_architecture, explain_antipattern"],
  ["MCP tools", "searchPatterns, recommendChecks"],
  ["MCP resources", "resource://patterns/*"],
  ["Source of truth", "Markdown packets + metadata"]
];

export function ProtocolPage() {
  return (
    <main>
      <PageHero eyebrow="Protocol surface" title="Expose the specification as resources, prompts, and tools.">
        <p>
          The public site is human-readable, but the same packet model can be served through MCP so
          coding agents can search, cite, and apply the practices without scraping pages.
        </p>
      </PageHero>

      <section className="section">
        <div className="container split">
          <div>
            <span className="lbl">Model</span>
            <h2 className="section__title">Stable packets over loose prose.</h2>
            <p className="split__copy">
              MCP resources expose canonical content, prompts turn that content into repeatable
              agent workflows, and tools provide search and recommendation entry points. Static
              JSON files in this prototype show the intended contract.
            </p>
            <div className="endpoint-list">
              {endpoints.map(([href, label]) => (
                <a key={href} href={href}>
                  <code>{href}</code>
                  <span className="lbl">{label}</span>
                </a>
              ))}
            </div>
          </div>
          <div className="cell cell--sunken protocol-stack" aria-label="MCP protocol stack diagram">
            <div className="cell__chrome"><span>The stack</span><span>top → bottom</span></div>
            {stack.map(([layer, detail]) => (
              <div key={layer} className="protocol-stack__layer cell cell--bare">
                <span className="lbl">{layer}</span>
                <strong>{detail}</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Section eyebrow="Interactive contract" title="Inspect example requests.">
        <p className="section__lede">
          Switch between the core request types and copy a payload into an MCP client or a future
          server test.
        </p>
        <ProtocolTabs examples={protocolExamples} />
      </Section>

      <section id="agent-skills" className="section">
        <div className="container split">
          <div>
            <span className="lbl">Skills package</span>
            <h2 className="section__title">Installable agent practice, not just documentation.</h2>
            <p className="split__copy">
              The package includes a Claude Code compatible skill. It describes when to use the
              specification, how to search it, and how to turn a pattern into concrete code review
              actions.
            </p>
            <a href="/skills/README.md" className="btn">Open skill docs <span className="btn__mark">↗</span></a>
          </div>
          <div className="install-console">
            <div className="install-console__top">
              <span className="lbl">claude-code skill install</span>
              <CopyButton text={installCommand} />
            </div>
            <pre><code>{installCommand}</code></pre>
          </div>
        </div>
      </section>

      <Section eyebrow="Protocol references" title="Aligned to MCP concepts." compact>
        <div className="source-strip">
          <a href="https://modelcontextprotocol.io/specification/2025-11-25" target="_blank" rel="noreferrer">MCP specification ↗</a>
          <a href="https://modelcontextprotocol.io/specification/2025-11-25/server/tools" target="_blank" rel="noreferrer">MCP tools ↗</a>
          <a href="https://modelcontextprotocol.io/specification/2025-11-25/server/prompts" target="_blank" rel="noreferrer">MCP prompts ↗</a>
          <a href="https://modelcontextprotocol.io/specification/2025-11-25/schema" target="_blank" rel="noreferrer">MCP schema ↗</a>
        </div>
      </Section>
    </main>
  );
}
